/**
 * Explicit language → approved agreement document mapping.
 *
 * Only languages with real approved content/files are marked available.
 * Do not add codes here until legal has supplied an official translation.
 */

export const AGREEMENT_DEFAULT_LANGUAGE = 'en';

/** Languages that may appear in the selector when configured for an agreement. */
export const AGREEMENT_LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English', suffix: 'EN' },
];

export const AGREEMENT_KEYS = {
  CONFIDENTIALITY: 'confidentiality',
  AML_POLICY: 'aml_policy',
};

/**
 * Catalog of agreements. `contentLangs` lists languages with approved display content.
 * `staticPdfByLang` is optional: public URL for a pre-approved PDF when one exists.
 * Currently only English HTML content is approved in-app; no static translated PDFs exist.
 */
export const AGREEMENT_CATALOG = {
  [AGREEMENT_KEYS.CONFIDENTIALITY]: {
    key: AGREEMENT_KEYS.CONFIDENTIALITY,
    title: 'Partner Confidentiality & Data Protection Undertaking',
    shortTitle: 'Confidentiality Agreement',
    downloadBaseName: 'Confidentiality_Agreement',
    acceptanceType: 'nda',
    contentLangs: ['en'],
    staticPdfByLang: {
      // No separate approved confidentiality PDF in public/ yet.
      // English download is generated from the approved in-app English content.
    },
  },
  [AGREEMENT_KEYS.AML_POLICY]: {
    key: AGREEMENT_KEYS.AML_POLICY,
    title: 'Anti-Money Laundering (AML) Policy Agreement',
    shortTitle: 'AML Policy Agreement',
    downloadBaseName: 'AML_Policy_Agreement',
    acceptanceType: 'aml_policy',
    contentLangs: ['en'],
    staticPdfByLang: {},
  },
};

export function getLanguageOption(code) {
  return AGREEMENT_LANGUAGE_OPTIONS.find((l) => l.code === code) || null;
}

export function getAgreementCatalogEntry(agreementKey) {
  return AGREEMENT_CATALOG[agreementKey] || null;
}

/** Languages offered for an agreement (union of catalog options that are listed for that doc). */
export function getAgreementLanguageOptions(agreementKey) {
  const entry = getAgreementCatalogEntry(agreementKey);
  if (!entry) return [];
  const allowed = new Set([...(entry.contentLangs || []), ...Object.keys(entry.staticPdfByLang || {})]);
  // Always include default language option metadata even if somehow missing from contentLangs
  allowed.add(AGREEMENT_DEFAULT_LANGUAGE);
  return AGREEMENT_LANGUAGE_OPTIONS.filter((l) => allowed.has(l.code));
}

export function isAgreementLanguageAvailable(agreementKey, languageCode) {
  const entry = getAgreementCatalogEntry(agreementKey);
  if (!entry || !languageCode) return false;
  if ((entry.contentLangs || []).includes(languageCode)) return true;
  if (entry.staticPdfByLang?.[languageCode]) return true;
  return false;
}

export function buildAgreementDownloadFilename(agreementKey, languageCode) {
  const entry = getAgreementCatalogEntry(agreementKey);
  const lang = getLanguageOption(languageCode) || getLanguageOption(AGREEMENT_DEFAULT_LANGUAGE);
  const base = entry?.downloadBaseName || 'Agreement';
  const suffix = lang?.suffix || String(languageCode || 'EN').toUpperCase();
  return `${base}_${suffix}.pdf`;
}

/**
 * Resolve what to show/download for a selected language.
 * Never reports a language as available when content is missing.
 */
export function resolveAgreementDocument(agreementKey, languageCode) {
  const entry = getAgreementCatalogEntry(agreementKey);
  const requested = languageCode || AGREEMENT_DEFAULT_LANGUAGE;
  const requestedLang = getLanguageOption(requested);
  const fallbackLang = getLanguageOption(AGREEMENT_DEFAULT_LANGUAGE);

  if (!entry) {
    return {
      status: 'unavailable',
      agreementKey,
      requestedLanguage: requested,
      displayLanguage: null,
      title: 'Agreement',
      staticPdfUrl: null,
      downloadFilename: buildAgreementDownloadFilename(agreementKey, requested),
      message: 'This agreement is not configured.',
      fallbackLanguage: AGREEMENT_DEFAULT_LANGUAGE,
      showFallbackContent: false,
    };
  }

  const available = isAgreementLanguageAvailable(agreementKey, requested);
  if (available) {
    return {
      status: 'available',
      agreementKey,
      requestedLanguage: requested,
      displayLanguage: requested,
      title: entry.title,
      staticPdfUrl: entry.staticPdfByLang?.[requested] || null,
      downloadFilename: buildAgreementDownloadFilename(agreementKey, requested),
      message: null,
      fallbackLanguage: null,
      showFallbackContent: false,
    };
  }

  const fallbackAvailable = isAgreementLanguageAvailable(agreementKey, AGREEMENT_DEFAULT_LANGUAGE);
  const label = requestedLang?.label || requested;
  return {
    status: 'unavailable',
    agreementKey,
    requestedLanguage: requested,
    displayLanguage: null,
    title: entry.title,
    staticPdfUrl: null,
    downloadFilename: buildAgreementDownloadFilename(agreementKey, requested),
    message: `${label} translation is not available for this agreement. An official approved translation has not been provided.`,
    fallbackLanguage: fallbackAvailable ? AGREEMENT_DEFAULT_LANGUAGE : null,
    fallbackLabel: fallbackLang?.label || 'English',
    showFallbackContent: fallbackAvailable,
  };
}

export function getSelectableAgreementLanguages(agreementKey) {
  // Selector lists configured language options for the agreement type.
  // Availability is resolved separately so unavailable picks are never mislabeled.
  return getAgreementLanguageOptions(agreementKey);
}
